import { memo } from 'react';
import { Check, Star, Sparkles } from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { trackCTAClick } from '@/hooks/useGoogleAnalytics';
import { staggerDelay } from '@/lib/stagger';

const pricingPlans = [
  {
    name: 'Primera consulta',
    price: '50€',
    description: 'Tu primer paso hacia una vida más saludable',
    features: [
      'Evaluación completa de tu estado de salud',
      'Análisis de hábitos alimenticios actuales',
      'Definición de objetivos personalizados',
      'Plan nutricional inicial',
      'Consulta online o presencial',
    ],
    cta: 'Reservar primera consulta',
    popular: false,
  },
  {
    name: 'Seguimiento individual',
    price: '45€',
    description: 'Mantén tu progreso con acompañamiento continuo',
    features: [
      'Revisión de progreso y métricas',
      'Ajuste del plan nutricional',
      'Educación nutricional',
      'Nuevas recetas adaptadas',
      'Seguimiento vía WhatsApp',
      'Consulta online o presencial',
    ],
    cta: 'Reservar seguimiento',
    popular: false,
  },
  {
    name: 'Bono 3 sesiones',
    price: '120€',
    originalPrice: '135€',
    description: 'La mejor opción para resultados duraderos',
    features: [
      '3 consultas de seguimiento',
      'Ahorro de 15€ garantizado',
      'Educación nutricional',
      'Seguimiento vía WhatsApp incluido',
      'Recetas personalizadas',
      'Flexibilidad online o presencial',
      'Prioridad en citas',
    ],
    cta: 'Obtener bono',
    popular: true,
  },
];

const Pricing = memo(() => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="precios" className="section-padding">
      <div className="container-custom">
        {/* Section Header */}
        <div
          ref={ref}
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-primary font-medium mb-4">
            Precios
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Tarifas de{' '}
            <span className="text-gradient">nutrición</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Tarifas transparentes y accesibles. Elige la opción que mejor 
            se adapte a tus necesidades.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {pricingPlans.map((plan, index) => (
            <PricingCard key={plan.name} plan={plan} index={index} />
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            ¿Tienes dudas sobre qué opción elegir?{' '}
            <a
              href="#contacto"
              className="text-primary font-medium hover:underline"
              onClick={() => trackCTAClick('Contáctame sin compromiso', 'Pricing')}
            >
              Contáctame sin compromiso
            </a>
          </p>
        </div>
      </div>
    </section>
  );
});

Pricing.displayName = 'Pricing';

interface PricingCardProps {
  plan: typeof pricingPlans[0];
  index: number;
}

const PricingCard = memo(({ plan, index }: PricingCardProps) => {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <div
      ref={ref}
      className={`relative glass-card h-full flex flex-col transition-all duration-700 ${
        plan.popular ? 'border-primary/30 md:scale-[1.03]' : ''
      } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${staggerDelay(index)}`}
    >
      {/* Popular badge */}
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="gradient-rose px-4 py-1.5 rounded-full flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">Más popular</span>
          </div>
        </div>
      )}

      {/* Plan Name */}
      <h3 className="text-xl font-semibold text-foreground mb-2">{plan.name}</h3>
      <div x-apple-data-detectors="false">
        <p className="text-muted-foreground text-sm mb-6">{plan.description}</p>

        {/* Price */}
        <div className="mb-6">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-foreground">{plan.price}</span>
          {plan.originalPrice && (
            <span className="text-lg text-muted-foreground line-through">
              {plan.originalPrice}
            </span>
          )}
        </div>
        {plan.originalPrice && (
          <span className="text-sm text-rose font-medium">
            ¡Ahorra 15€!
          </span>
        )}
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-grow">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
              plan.popular ? 'bg-rose-light' : 'bg-sage-light'
            }`}>
              <Check className={`w-3 h-3 ${plan.popular ? 'text-rose-dark' : 'text-sage-dark'}`} />
            </div>
            <span className="text-muted-foreground text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href="#contacto"
        className={`block text-center py-3 px-6 rounded-xl font-medium transition-all duration-300 ${
          plan.popular
            ? 'gradient-rose text-white hover:shadow-lg hover:scale-[1.02]'
            : 'btn-secondary'
        }`}
        onClick={() => trackCTAClick(plan.cta, 'Pricing')}
      >
        {plan.cta}
      </a>
    </div>
  );
});

PricingCard.displayName = 'PricingCard';

export default Pricing;
